import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Clock, Trophy, Users, Calendar, ArrowRight, Zap, Sparkles, Play, ShieldCheck } from 'lucide-react';
import Countdown from './Countdown';
import { formatCurrency, formatDateTime, cn } from '@/lib/utils';
import { toast } from '../lib/swal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WaitingSorteoSection = ({ sorteo, countdown, user }) => {
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const mainImage = sorteo.imagenes?.[0] ?
    (sorteo.imagenes[0].startsWith('http') ? sorteo.imagenes[0] : `${BACKEND_URL}${sorteo.imagenes[0]}`) : null;

  const handleInitLiveDraw = async () => {
    try {
      await axios.post(`${API}/admin/live-draw/init/${sorteo.id}`);
      toast.success('Sorteo inicializado exitosamente');
      navigate(`/sorteo/${sorteo.landing_slug}`);
    } catch (error) {
      toast.error('Error al inicializar el sorteo');
    }
  };

  return (
    <section className="relative min-h-[650px] flex items-center overflow-hidden border-b border-white/5 bg-slate-950 py-20">

      {/* 🌌 FONDO ESCÉNICO - Mayor oscuridad para contraste */}
      <div className="absolute inset-0 z-0">
        {mainImage ? (
          <div className="relative w-full h-full">
            <img src={mainImage} className="w-full h-full object-cover opacity-20 blur-sm scale-105" alt="" />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/90 to-slate-950" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-transparent to-slate-950 opacity-80" />
          </div>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-orange-950/30 via-slate-950 to-purple-950/30" />
        )}

        {/* Luces y efectos corregidos para visibilidad */}
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-orange-500/10 rounded-full blur-[150px] animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[150px] animate-pulse delay-700" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* COLUMNA IZQUIERDA: INFO & PROGRESO */}
          <div className="space-y-10 animate-in fade-in slide-in-from-left-8 duration-1000">
            <div className="space-y-6">
              <div className="flex flex-wrap gap-3">
                <Badge className="bg-orange-500 text-white border-none px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.3em] shadow-[0_0_20px_rgba(234,88,12,0.4)]">
                  <Clock className="w-3.5 h-3.5 mr-2 animate-spin-slow" />
                  Sorteo por Iniciar
                </Badge>
                {sorteo.estado === 'ready' && (
                  <Badge className="bg-green-500 text-white border-none px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.3em] shadow-[0_0_20px_rgba(34,197,94,0.4)]">
                    <Zap className="w-3.5 h-3.5 mr-2" />
                    Listo para el Envivo
                  </Badge>
                )}
              </div>

              <h2 className="text-xl md:text-5xl  font-black text-white leading-[0.9] tracking-tighter uppercase italic drop-shadow-[0_10px_10px_rgba(0,0,0,0.5)]">
                {sorteo.titulo}
              </h2>

              <p className="text-xl md:text-2xl text-slate-300 font-medium max-w-xl leading-relaxed">
                Estamos afinando los detalles finales. <span className="text-orange-500 font-black italic">¡El panel de lotería está casi listo!</span>
              </p>
            </div>

            {/* BARRA DE PROGRESO PREMIUM - Mayor Contraste */}
            {sorteo.mostrar_progreso !== false && (
              <div className="space-y-4 max-w-md bg-white/5 p-6 rounded-[2rem] border border-white/10 backdrop-blur-sm">
                <div className="flex justify-between items-end">
                  <span className="text-xs font-black text-orange-500 uppercase tracking-widest animate-pulse">
                    META DE VENTAS ALCANZADA: {Math.round(sorteo.progreso_porcentaje)}%
                  </span>
                  <div className="flex items-center gap-2 text-white/60">
                    <Users className="w-4 h-4 text-orange-500" />
                    <span className="text-[10px] font-black uppercase tracking-widest">{sorteo.cantidad_reservada || 0} BOLETOS</span>
                  </div>
                </div>
                <div className="h-6 bg-black/40 rounded-full p-1.5 border border-white/10 shadow-inner overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-orange-600 via-orange-400 to-amber-300 transition-all duration-[2000ms] shadow-[0_0_25px_rgba(234,88,12,0.6)] relative"
                    style={{ width: `${Math.max(5, sorteo.progreso_porcentaje)}%` }}
                  >
                    <div className="absolute inset-0 bg-white/30 animate-shimmer rounded-full" />
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-6 pt-4">
            {/* Botón removido de aquí para ponerlo en el card derecho por UX */}
            </div>
          </div>

          {/* COLUMNA DERECHA: COUNTDOWN IMPACTANTE */}
          <div className="relative animate-in zoom-in duration-1000 delay-300">
            <div className="absolute inset-0 bg-orange-500/10 blur-[100px] rounded-full" />

            <div className="relative p-10 md:p-14 bg-slate-900/60 backdrop-blur-3xl border-2 border-white/10 rounded-[4rem] shadow-2xl flex flex-col items-center text-center space-y-10 group hover:border-orange-500/40 transition-all duration-700">

              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-orange-600 to-amber-500 rounded-full text-xs font-black text-white uppercase tracking-[0.2em] mb-4 shadow-xl shadow-orange-500/20">
                  <Sparkles className="w-4 h-4" />
                  Sorteo Inminente
                </div>
                <h3 className="text-3xl font-black text-white uppercase italic tracking-tighter">COMIENZA EN</h3>
              </div>

              <div className="w-full">
                <div className="flex gap-4 md:gap-8 items-center justify-center">
                  <div className="space-y-3">
                    <div className="w-28 h-28 md:w-36 md:h-36 bg-black/60 border-2 border-white/10 rounded-[2.5rem] flex items-center justify-center text-6xl md:text-8xl font-black text-white shadow-2xl group-hover:border-orange-500 transition-colors">
                      {String(countdown?.minutos || 0).padStart(2, '0')}
                    </div>
                    <p className="text-[10px] font-black text-white/30 uppercase tracking-[0.4em]">Minutos</p>
                  </div>
                  <span className="text-5xl md:text-8xl font-black text-orange-500 animate-pulse">:</span>
                  <div className="space-y-3">
                    <div className="w-28 h-28 md:w-36 md:h-36 bg-black/60 border-2 border-white/10 rounded-[2.5rem] flex items-center justify-center text-6xl md:text-8xl font-black text-white shadow-2xl group-hover:border-orange-500 transition-colors">
                      {String(countdown?.segundos || 0).padStart(2, '0')}
                    </div>
                    <p className="text-[10px] font-black text-white/30 uppercase tracking-[0.4em]">Segundos</p>
                  </div>
                </div>
              </div>

              <div className="w-full pt-8 h-40">
                {isAdmin && (sorteo.estado === 'ready' || sorteo.estado === 'waiting') ? (
                  <Button
                    onClick={handleInitLiveDraw}
                    className="w-full h-full rounded-[2.5rem] bg-gradient-to-br from-orange-600 to-amber-500 hover:from-orange-500 hover:to-amber-400 text-white shadow-2xl shadow-orange-500/30 border-none flex flex-col items-center justify-center gap-3 group/admin active:scale-95 transition-all"
                  >
                    <Play className="w-10 h-10 fill-white group-hover/admin:scale-110 transition-transform" />
                    <span className="text-sm font-black uppercase italic tracking-[0.2em]">INICIAR SORTEO EN VIVO</span>
                  </Button>
                ) : (
                  <Link to={`/sorteo/${sorteo.landing_slug}`} className="block w-full h-full">
                    <div className="w-full h-full rounded-[2.5rem] border-2 border-dashed border-white/10 flex flex-col items-center justify-center hover:bg-white/5 transition-all text-slate-400 group/link">
                      <ArrowRight className="w-8 h-8 mb-2 group-hover/link:translate-x-3 transition-transform text-orange-500" />
                      <span className="text-[10px] font-black uppercase tracking-[0.3em]">Ver Detalles del Sorteo</span>
                    </div>
                  </Link>
                )}
              </div>

              <div className="absolute -top-6 -right-6 w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 rounded-3xl flex items-center justify-center shadow-2xl rotate-12 group-hover:rotate-0 transition-transform duration-500 border-4 border-white/10">
                <Trophy className="w-10 h-10 text-white drop-shadow-lg" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WaitingSorteoSection;
